/**
 * useAccountProvisioning — estado real de la cuenta según el BE (bloque
 * `account_provisioning` de `/api/v1/me`).
 *
 * El BE ya calcula qué falta para que la cuenta opere y cuál es el siguiente
 * paso, con su etiqueta y su ruta. El FE llevaba tiempo ignorándolo y
 * derivando su propia versión a partir de `party.kycLevel` y `account.clabe`,
 * que no distingue "falta que completes el onboarding" de "tu CLABE está en
 * trámite" — y afirmaba tiempos de espera que nadie había prometido.
 *
 * A diferencia de `useProductAvailability`, aquí NO se asume nada por defecto:
 * sin dato (BE viejo, sin sesión, query en vuelo) se devuelve `null` y quien
 * consuma decide. Inventar un estado es justo el problema que esto viene a
 * cerrar.
 */
import { useQuery } from '@tanstack/react-query';

import { clientApi } from '@tankef/api-client';
import type { AccountProvisioning } from '@tankef/core';

export interface AccountProvisioningResult {
  provisioning: AccountProvisioning | null;
  isLoading: boolean;
}

export function useAccountProvisioning(enabled = true): AccountProvisioningResult {
  const query = useQuery({
    queryKey: ['me', 'account-provisioning'],
    queryFn: () => clientApi.meProvisioning(),
    enabled,
    // Mismo tiempo que `useCurrentPartyProfile`: el estado cambia cuando el
    // usuario avanza en su onboarding o el proveedor asigna la CLABE, no cada
    // segundo — pero tampoco conviene servirlo rancio media hora.
    staleTime: 60_000,
  });

  return {
    provisioning: query.data ?? null,
    isLoading: query.isLoading,
  };
}
